import { WSButton } from "@wingspanhq/fe-component-library";
import { useEffect, useMemo, useRef } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { queriesExternalBankAccounts, queriesInternalAccounts } from "@query";
import { useQueryOwnPayoutSettings } from "@hooks/useQueryOwnPayoutSettings";
import { selectorPayoutSettingsDefaultDestination } from "@selectors/selectorPayoutSettingsDefaultDestination";
import { useUserId } from "../../../../query/hooks/helpers";
import { useClientQuery } from "../../../../query/users/queries";
import { selectorAccountBalance } from "../../../../shared/selectors/selectorAccountBalance";
import { selectorActiveBankingAccount } from "../../../../shared/selectors/selectorActiveBankingAccount";
import { selectorActiveTaxWithholdingAccount } from "../../../../shared/selectors/selectorActiveTaxWithholdingAccount";
import {
  SelectMethod,
  SelectMethodProps
} from "../../../Settings/components/SelectMethod";
import { PATH_TRANSFER_ADD_BANK_ACCOUNT } from "../../paths";
import { TransferBalance, TransferType } from "../../types";
import { selectorExternalBankAccountDepositReady } from "@selectors/selectorExternalBankAccountDepositReady";
import { selectorExternalBankAccountWithdrawalReady } from "@selectors/selectorExternalBankAccountWithdrawalReady";

type DropdownProps = {
  balance: TransferBalance;
  type: TransferType;
} & Omit<SelectMethodProps, "data">;

export const DropdownAccount: React.FC<DropdownProps> = props => {
  const history = useHistory();
  const location = useLocation();

  const userId = useUserId();
  const queryExternalBankAccounts =
    queriesExternalBankAccounts.listExternalBankAccounts.useQuery();
  const queryInternalAccounts =
    queriesInternalAccounts.listInternalAccounts.useQuery();
  const queryClient = useClientQuery(userId);
  const queryPayoutSettings = useQueryOwnPayoutSettings();

  const activeBankingAccount = useMemo(() => {
    return queryInternalAccounts.data?.data?.find(selectorActiveBankingAccount);
  }, [queryInternalAccounts.data]);

  const activeTaxWithholdingAccount = useMemo(() => {
    return queryInternalAccounts.data?.data?.find(
      selectorActiveTaxWithholdingAccount
    );
  }, [queryInternalAccounts.data]);

  const queryBankingBalance =
    queriesInternalAccounts.getInternalAccountBalance.useQuery(
      { internalAccountId: activeBankingAccount?.id! },
      { enabled: !!activeBankingAccount }
    );

  const queryTaxWithholdingBalance =
    queriesInternalAccounts.getInternalAccountBalance.useQuery(
      { internalAccountId: activeTaxWithholdingAccount?.id! },
      { enabled: !!activeTaxWithholdingAccount }
    );

  const isLoading =
    queryExternalBankAccounts.isInitialLoading ||
    queryInternalAccounts.isInitialLoading ||
    queryClient.isInitialLoading ||
    queryPayoutSettings.isInitialLoading ||
    queryBankingBalance.isInitialLoading ||
    queryTaxWithholdingBalance.isInitialLoading;

  const externalBankAccounts = useMemo(() => {
    const filterFn =
      props.type === "deposit"
        ? selectorExternalBankAccountDepositReady
        : selectorExternalBankAccountWithdrawalReady;
    return queryExternalBankAccounts.data?.data?.filter(filterFn);
  }, [props.type, queryExternalBankAccounts.data]);

  const internalAccounts = useMemo(() => {
    if (
      props.balance === "wallet" &&
      activeTaxWithholdingAccount &&
      !!queryTaxWithholdingBalance.data &&
      selectorAccountBalance(queryTaxWithholdingBalance.data).balance > 0
    ) {
      return [activeTaxWithholdingAccount];
    }

    if (
      props.balance === "taxWithholdings" &&
      activeBankingAccount &&
      !!queryBankingBalance.data &&
      selectorAccountBalance(queryBankingBalance.data).balance > 0
    ) {
      return [activeBankingAccount];
    }
  }, [
    activeBankingAccount,
    activeTaxWithholdingAccount,
    props.balance,
    queryBankingBalance.data,
    queryTaxWithholdingBalance.data
  ]);

  const refIsDefaultSet = useRef(false);

  useEffect(() => {
    if (refIsDefaultSet.current) return;

    if (props.type === "deposit") {
      const defaultPaymentMethodId =
        queryClient.data?.profile.defaultPaymentMethod?.accountId;

      if (defaultPaymentMethodId) {
        const matchingExternalBankAccount = externalBankAccounts?.find(
          externalBankAccount =>
            externalBankAccount.id === defaultPaymentMethodId
        );

        if (matchingExternalBankAccount) {
          props.onChange({
            id: defaultPaymentMethodId,
            type: "Account"
          });
          refIsDefaultSet.current = true;
        }
      }
    } else {
      if (!queryPayoutSettings.data) return;

      const defaultPayoutMethodId = selectorPayoutSettingsDefaultDestination(
        queryPayoutSettings.data
      )?.id;

      if (defaultPayoutMethodId) {
        const matchingExternalBankAccount = externalBankAccounts?.find(
          externalBankAccount =>
            externalBankAccount.id === defaultPayoutMethodId
        );

        if (matchingExternalBankAccount) {
          props.onChange({
            id: defaultPayoutMethodId,
            type: "Account"
          });
          refIsDefaultSet.current = true;
        }
      }
    }
  }, [
    externalBankAccounts,
    props,
    queryClient.data?.profile.defaultPaymentMethod?.accountId,
    queryPayoutSettings.data
  ]);

  return isLoading ||
    externalBankAccounts?.length ||
    internalAccounts?.length ? (
    <SelectMethod
      status={isLoading ? "loading" : undefined}
      data={{
        accounts: externalBankAccounts,
        internalAccounts
      }}
      value={props.value}
      onChange={props.onChange}
      menuFooterText="Add a new bank account"
      menuFooterAction={{
        label: "Add account",
        onClick: () => {
          history.push(PATH_TRANSFER_ADD_BANK_ACCOUNT + location.search);
        }
      }}
    />
  ) : (
    <WSButton.Tertiary
      fullWidth
      onClick={() =>
        history.push(PATH_TRANSFER_ADD_BANK_ACCOUNT + location.search)
      }
    >
      Add bank account
    </WSButton.Tertiary>
  );
};
